import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useAbortMasterUploadMutation, useCompleteMasterUploadMutation } from '../../../../redux/api/fymAPI';
import { CreateMasterUploadResponse } from '../../../../redux/api/fymAPI.types';
import { isStoppedError, MasterPartsStop, uploadMasterParts } from './masterUploadParts';
import { masterUploadErrorMessage, MasterUploadStage, reportMasterUploadError } from './masterUploadErrors';
import { endPendingMasterComplete, startPendingMasterComplete } from './pendingMasterCompletes';

interface UseMasterPartsSenderProps {
  accountId: number;
  setProgress: React.Dispatch<React.SetStateAction<number | null>>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
}

/**
 * Steps 2 and 3 of `useMasterUpload`: the parts go to S3, then the complete call saves the master.
 *
 * `cancel` (Stop, or an unmount) aborts the S3 upload while the parts go up. Once the complete call
 * runs, nothing stops it: the master is never lost half way. `stops` goes up on every stop, so an
 * upload whose create answer comes after the stop aborts at once.
 */
export function useMasterPartsSender({ accountId, setProgress, setError }: UseMasterPartsSenderProps) {
  const intl = useIntl();
  const [completeUpload] = useCompleteMasterUploadMutation();
  const [abortUpload] = useAbortMasterUploadMutation();
  // The upload on the way, so a stop or an unmount can abort it.
  const active = useRef<{ stop: MasterPartsStop; abort: () => void } | null>(null);
  // True while the complete call runs: then a stop or an unmount leaves the upload alone.
  const [isCompleting, setIsCompleting] = useState(false);
  const completing = useRef(false);
  const stops = useRef(0);

  const cancel = useCallback(() => {
    if (completing.current) {
      return;
    }
    stops.current += 1;
    active.current?.stop.stop();
    active.current?.abort();
    active.current = null;
  }, []);

  useEffect(() => cancel, [cancel]);

  const sendParts = useCallback(
    async (file: File, uuid: string, created: CreateMasterUploadResponse, stopsAtStart: number) => {
      const stop = new MasterPartsStop();
      const ids = { accountId, itemUuid: uuid, uploadId: created.uploadId, key: created.key };
      const abort = () => {
        abortUpload(ids).catch(console.error);
      };
      if (stops.current !== stopsAtStart) {
        abort();
        return 'failed' as const;
      }
      active.current = { stop, abort };
      let stage: MasterUploadStage = 'parts';
      try {
        const parts = await uploadMasterParts({
          file,
          parts: created.parts,
          partSizeBytes: created.partSizeBytes,
          stop,
          onProgress: (sent) => setProgress(Math.min(100, Math.round((sent / Math.max(file.size, 1)) * 100))),
        });
        stage = 'complete';
        completing.current = true;
        setIsCompleting(true);
        startPendingMasterComplete(uuid, file.name);
        try {
          await completeUpload({ ...ids, parts }).unwrap();
        } finally {
          endPendingMasterComplete(uuid);
          completing.current = false;
          setIsCompleting(false);
        }
        return 'saved' as const;
      } catch (failure) {
        if (!isStoppedError(failure) && active.current?.stop === stop) {
          abort();
        }
        if (!stop.stopped) {
          reportMasterUploadError(stage, failure, file, created.parts[0]?.url);
          setError(masterUploadErrorMessage(intl, failure));
        }
        return 'failed' as const;
      } finally {
        if (active.current?.stop === stop) {
          active.current = null;
        }
      }
    },
    [abortUpload, accountId, completeUpload, intl, setError, setProgress]
  );

  return { sendParts, cancel, stops, isCompleting };
}
